// Style
import mainStyle from "./mainStyle.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import { cloneDeep, set } from "lodash";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// MUI
import { useTheme } from "@mui/material/styles";
import {
  Divider,
  Grid,
  Box,
  Typography,
  Snackbar,
  Alert
} from "@mui/material";

// Components
import TabsField from "components/Tab";
import TableField from "components/Table";
import ActionSelector from "./components/ActionSelector";
import PendingChildren from './components/PendingChildren';
import SearchInput from "components/SearchInput";
import ColumnFilterToggle from "components/Table/ColumnFilterToggle";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import DrawerField from "components/Drawer";
import GlobalAlert from "components/Notification";

// IMG
import { ReactComponent as DisplayClockIcon } from "assets/images/icons/icn_display_clock.svg";
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import icon_refresh from "assets/images/icons/icn_refresh.svg";
import { SolidButton } from "components/buttons/SolidButton";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Helper
import { exportTableDataToCSV } from "utils/export";
import { getMainSendRequestParams, initTableData } from "./helper";

// Const
import { defaultTabs, getMainTableColumns, customGroupDefinition, redirectionDeviceTypeDefinition } from "./const";

const Clients = () => {
  const [uuids] = useState(() => ({
    main: uuidv4()
  }))
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateClientId } = location?.state ?? {};

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState({
    all: [],
    wired: [],
    wireless: [],
  });
  const [tabs, setTabs] = useState(cloneDeep(defaultTabs));
  const activeTab = useMemo(() => tabs.find(tab => tab.isActive), [tabs]);
  const isEmptyTable = useMemo(() => {
    const tabValue = activeTab?.value;

    if (tabValue === "all") {
      return tableData.all.length === 0;
    } else if (tabValue === "wired") {
      return tableData.wired.length === 0;
    } else if (tabValue === "wireless") {
      return tableData.wireless.length === 0;
    }

    return true;
  }, [activeTab, tableData]);
  const [isShowActionButton, setIsShowActionButton] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentDrawerData, setCurrentDrawerData] = useState(null);

  const handelConnectedTo = client => {
    const { connectedToDeviceName, connectedToDeviceId } = client;

    navigate("/site/devices", {
      state: {
        navigateDeviceName: connectedToDeviceName,
      }
    });
  }

  const handelInterface = client => {
    const { band } = client;
    const isWireless = !!band;
    let state = {};

    if (isWireless) {
      state = {
        navigateTab: "wireless-ssid",
        navigateSelectedSsidName: client.ssid,
      }

    } else {
      state = {
        navigateTab: "wired-port-management",
        navigatePortDeviceType: redirectionDeviceTypeDefinition[client.connectedToDeviceType],
        navigateSelectedPort: client.port ?? +client.interface,
      }
    }

    navigate("/site/settings", {
      state
    });
  }

  const allTableInstance = useTableInstance({
    tableName: "AllClients",
    data: tableData["all"],
    columns: getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      callbacks: {
        handelConnectedTo,
        handelInterface
      }
    }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: [
      "status",
      "clientName",
      "manufacturer",
      "ipAddress",
      "authenticationType",
      "connectedToDeviceName",
      "interface",
      "vlan",
      "ssid",
      "channel",
      "formattedUpTime"
    ],
    enableSorting: true,
    hiddenColumns: [
      "formattedSignal",
      "snr",
      "formattedDownload",
      "formattedUpload",
      "formattedTraffic",
      "formattedUpTime",
    ],
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });
  const wiredTableInstance = useTableInstance({
    tableName: "WiredClients",
    data: tableData["wired"],
    columns: getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      callbacks: {
        handelConnectedTo,
        handelInterface
      }
    }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: [
      "status",
      "clientName",
      "manufacturer",
      "ipAddress",
      "authenticationType",
      "connectedToDeviceName",
      "interface",
      "vlan",
      "ssid",
      "channel",
      "upTime"
    ],
    enableSorting: true,
    hiddenColumns: [
      "formattedSignal",
      "snr",
      "formattedDownload",
      "formattedUpload",
      "formattedTraffic",
      "formattedUpTime",
    ],
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });
  const wirelessTableInstance = useTableInstance({
    tableName: "WirelessClients",
    data: tableData["wireless"],
    columns: getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      callbacks: {
        handelConnectedTo,
        handelInterface
      }
    }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: [
      "status",
      "clientName",
      "manufacturer",
      "ipAddress",
      "authenticationType",
      "connectedToDeviceName",
      "interface",
      "vlan",
      "ssid",
      "channel",
      "formattedUpTime"
    ],
    enableSorting: true,
    hiddenColumns: [
      "formattedSignal",
      "snr",
      "formattedDownload",
      "formattedUpload",
      "formattedTraffic",
      "formattedUpTime",
    ],
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });

  const currentTableInstance = useMemo(() => {
    const tabValue = activeTab?.value;
    if (tabValue === "all") {
      return allTableInstance;
    } else if (tabValue === "wired") {
      return wiredTableInstance;
    } else if (tabValue === "wireless") {
      return wirelessTableInstance;
    }
    return null;
  }, [activeTab, allTableInstance, wiredTableInstance, wirelessTableInstance]);

  const tabTableData = useMemo(() => {
    const tabValue = activeTab?.value;
    if (tabValue === "all") {
      return tableData.all;
    } else if (tabValue === "wired") {
      return tableData.wired;
    } else if (tabValue === "wireless") {
      return tableData.wireless;
    }
    return [];
  }, [activeTab, tableData]);

  const selectedInstancesList = useMemo(() => {
    const tabValue = activeTab?.value;
    if (tabValue === "all") {
      return allTableInstance && allTableInstance.table.getSelectedRowModel().rows;
    } else if (tabValue === "wired") {
      return wiredTableInstance && wiredTableInstance.table.getSelectedRowModel().rows;
    } else if (tabValue === "wireless") {
      return wirelessTableInstance && wirelessTableInstance.table.getSelectedRowModel().rows;
    }
  }, [activeTab, allTableInstance, wiredTableInstance, wirelessTableInstance]);

  const fetchTableData = () => {
    if (isAuthReady) {
      const { apiKey, restfulParams } = getMainSendRequestParams({ auth });

      sendRequest(
        apiKey,
        uuids.main,
        restfulParams
      );
    }
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setCurrentDrawerData(null);
  }

  useEffect(() => {
    fetchTableData();
  }, [isAuthReady]);

  useEffect(() => {
    const response = getData(uuids.main);
    const responseData = response?.data ?? null;

    if (responseData) {
      setIsLoading(false);

      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: responseData.error?.message || 'Failed to get clients.',
        });
      } else {

        const tmpTableData = initTableData({
          responseData
        });

        const allClients = tmpTableData;
        const wiredClients = tmpTableData.filter(item => !item.ssid); // Assume SSID presence indicates wireless?
        const wirelessClients = tmpTableData.filter(item => item.ssid);

        setTableData({
          all: allClients,
          wired: wiredClients,
          wireless: wirelessClients,
        });

        setTabs(prevTabs => {
          const newTabs = prevTabs.map(tab => {
            switch (tab.value) {
              case 'all': return { ...tab, tabCount: allClients.length };
              case 'wired': return { ...tab, tabCount: wiredClients.length };
              case 'wireless': return { ...tab, tabCount: wirelessClients.length };
              default: return tab;
            }
          });

          return newTabs;
        })

        setIsLoading(false);

        // if selected drawer client exists in new data, update it
        if (currentDrawerData) {
          const updatedClient = allClients.find(item => item.clientId === currentDrawerData.clientId);
          if (updatedClient) {
            setCurrentDrawerData(updatedClient);
          }
        }
      }
    }
  }, [dataMap]);

  useEffect(() => {
    if (navigateClientId) {
      const matchedClient = tableData.all.find(client => client.clientId === navigateClientId)

      if (matchedClient) {
        setCurrentDrawerData(structuredClone(matchedClient));
        setDrawerOpen(true);

        const {
          navigateClientId,
          ...resetStates
        } = location?.state;

        navigate(location.pathname, {
          replace: true,
          state: resetStates
        });
      }
    }
  }, [navigateClientId, tableData.all])

  return (
    <>
      <Box className={`main-card-content ${isEmptyTable ? "main-card-content__empty_table" : ""}`}>
        <Box>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ height: "40px" }}>
              <Grid container sx={{ minHeight: "100%" }} alignItems="center">
                {
                  selectedInstancesList?.length > 0 && (
                    <>
                      <ActionSelector
                        {...{
                          setAlertInfo,
                          tableInstance: currentTableInstance,
                          isShowActionButton,
                          setIsShowActionButton,
                          selectedInstancesList,
                          initCallback: () => {
                            fetchTableData();
                          }
                        }}
                      />
                      <Divider
                        sx={{ width: "1px", margin: "0 20px" }}
                        orientation="vertical"
                        flexItem
                      />
                    </>
                  )
                }

                <TabsField
                  tabVal={activeTab?.value}
                  handleTabsChange={(e, newValue) => {
                    setTabs(prevTabs => prevTabs.map(tab => ({
                      ...tab,
                      isActive: tab.value === newValue
                    })));
                  }}
                  tabData={tabs}
                />

              </Grid>
            </Box>

            <Box sx={{ height: "80px" }}>
              <Grid container sx={{ minHeight: "100%" }} alignItems="center" gap={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DisplayClockIcon />
                  <Typography sx={{ marginLeft: "5px" }}>Last 24 Hours</Typography>
                </Box>
                <Divider
                  sx={{ width: "1px", height: "40px", margin: "20px 20px 0" }}
                  orientation="vertical"
                  flexItem
                />

                {/* Search */}
                <SearchInput
                  value={currentTableInstance?.globalFilter}
                  onChange={currentTableInstance?.setGlobalFilter}
                  placeholder="Search by Name"
                />

                {/* Advanced Search */}
                <ColumnFilterToggle
                  {...currentTableInstance}
                  filterData={[
                    "status",
                    "manufacturer",
                    "authenticationType",
                    "connectedToDeviceName",
                    "interface",
                    "vlan",
                    "ssid",
                    "channel",
                    "formattedLastSeen",
                    "formattedSignal"
                  ]}
                />

                {/* Column visibility */}
                <ColumnVisibilityToggle
                  maxWidth={600}
                  {...currentTableInstance}
                  groupedData={customGroupDefinition}
                  hiddenCheck={["status", "clientName"]}
                />

                {/* Refresh */}
                <Box
                  margin="8px 5px 0"
                  sx={{ cursor: "pointer" }}
                  onClick={fetchTableData}
                >
                  <img src={icon_refresh} alt="" />
                </Box>

                <SolidButton
                  startIcon={<ExportIcon />}
                  width="100px"
                  height="32px"
                  onClick={() =>
                    exportTableDataToCSV(
                      currentTableInstance,
                      "export.csv",
                      { emptyValue: "" }
                    )
                  }
                >
                  Export
                </SolidButton>
              </Grid>
            </Box>

          </Grid>

          <TableField
            {...currentTableInstance}
            pendingChildren={
              <PendingChildren
                {...{
                  isLoading,
                  list: tabTableData,
                  description: "No data"
                }}
              />
            }
            drawerOpen={drawerOpen}
            handleRowOnClick={(row) => {
              setCurrentDrawerData(row);
              setDrawerOpen(true);
            }}
          />
        </Box>
      </Box>
      {
        drawerOpen &&
        <DrawerField
          contentKey={`Clients_${uuidv4()}`}
          open={drawerOpen}
          width="550px"
          title="Client Details"
          handleClose={handleDrawerClose}
          commonContent="clients"
          contentProps={{
            auth,
            selectedClientId: currentDrawerData.clientId,
            onDrawerApplyCallback: () => {
              fetchTableData();
            },
          }}
        />
      }

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default Clients;
